import {
  DataTable,
  DescriptionList,
  InputField,
  Modal,
  type DataTableColumn,
  type DataTableRow,
} from '@enamelai/design-system'
import { InvoiceEntry, LedgerEntry } from '@dataTypes/billing.types'
import { formatCurrency, formatInvoiceNumber } from '@utils/billing.utils'
import { logger } from '@utils/safeLogger'
import { format } from 'date-fns'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useReactToPrint } from 'react-to-print'

import './PrintInvoiceModalStyle.css'

interface PrintInvoiceModalProps {
  show: boolean
  onHide: () => void
  invoice: InvoiceEntry | null
}

const DUE_DATE_DISPLAY_FORMAT = 'MM / dd / yyyy'

const PROCEDURE_COLUMNS: DataTableColumn[] = [
  { id: 'procedureCode', label: 'Code', width: '6%' },
  { id: 'description', label: 'Description', width: '20%' },
  { id: 'ts', label: 'T/S', width: '6%' },
  { id: 'clinicFee', label: 'Charges', width: '10%' },
  { id: 'estimatedInsurance', label: 'Estimated Ins.', width: '10%' },
  { id: 'approvedInsurance', label: 'Approved Ins.', width: '10%' },
  { id: 'discount', label: 'Discount', width: '10%' },
  { id: 'appointmentAdj', label: 'Appt. adj.', width: '10%' },
  { id: 'patientResponsibility', label: 'Patient resp.', width: '10%' },
]

const calculateEntryTotals = (
  entry: LedgerEntry,
): {
  charges: number
  estimatedIns: number
  approvedIns: number
  discount: number
  appointmentAdj: number
  patientResp: number
} =>
  entry.procedures.reduce(
    (acc, procedure) => ({
      charges: acc.charges + (procedure.clinicFee || 0),
      estimatedIns: acc.estimatedIns + (procedure.estimatedInsurance || 0),
      approvedIns: acc.approvedIns + (procedure.approvedInsurance || 0),
      discount: acc.discount + (procedure.discount || 0),
      appointmentAdj:
        acc.appointmentAdj +
        (procedure.patientAdjustment || 0) +
        (procedure.insuranceAdjustment || 0),
      patientResp: acc.patientResp + (procedure.patientResponsibility || 0),
    }),
    {
      charges: 0,
      estimatedIns: 0,
      approvedIns: 0,
      discount: 0,
      appointmentAdj: 0,
      patientResp: 0,
    },
  )

const buildProcedureRows = (entry: LedgerEntry): DataTableRow[] => {
  const entryTotals = calculateEntryTotals(entry)
  const procedureRows: DataTableRow[] = entry.procedures.map((procedure) => ({
    id: procedure.procedureLogId,
    procedureCode: procedure.procedureCode,
    description: procedure.description,
    ts: procedure.toothDetails || '',
    clinicFee: formatCurrency(procedure.clinicFee),
    estimatedInsurance: formatCurrency(procedure.estimatedInsurance || 0),
    approvedInsurance: formatCurrency(procedure.approvedInsurance || 0),
    discount: formatCurrency(procedure.discount || 0),
    appointmentAdj: formatCurrency(
      (procedure.patientAdjustment || 0) + (procedure.insuranceAdjustment || 0),
    ),
    patientResponsibility: formatCurrency(procedure.patientResponsibility),
  }))

  return [
    ...procedureRows,
    {
      id: `totals-${entry.appointmentId}`,
      procedureCode: 'Totals',
      description: '',
      ts: '',
      clinicFee: formatCurrency(entryTotals.charges),
      estimatedInsurance: formatCurrency(entryTotals.estimatedIns),
      approvedInsurance: formatCurrency(entryTotals.approvedIns),
      discount: formatCurrency(entryTotals.discount),
      appointmentAdj: formatCurrency(entryTotals.appointmentAdj),
      patientResponsibility: formatCurrency(entryTotals.patientResp),
    },
  ]
}

const PrintInvoiceModal: React.FC<PrintInvoiceModalProps> = ({
  show,
  onHide,
  invoice,
}) => {
  const [dueDate, setDueDate] = useState<Date>(new Date())
  const printRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (show && invoice) {
      if (invoice.dueDate) {
        const parsedDate = new Date(invoice.dueDate)
        if (!isNaN(parsedDate.getTime())) {
          setDueDate(parsedDate)
        } else {
          setDueDate(new Date())
        }
      } else {
        setDueDate(new Date())
      }
    }
  }, [show, invoice])

  const dueDateValue = useMemo(
    () => format(dueDate, DUE_DATE_DISPLAY_FORMAT),
    [dueDate],
  )

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: `Invoice ${formatInvoiceNumber(invoice?.invoiceNo || '')}`,
  })

  const handlePrintClick = async (): Promise<void> => {
    try {
      await handlePrint()
    } catch (error) {
      logger.error('Print failed:', error)
    }
  }

  if (!invoice) return null

  return (
    <Modal
      title="Print invoice"
      open={show}
      onClose={onHide}
      size="lg"
      className="print-invoice-modal"
      primaryLabel="Print"
      secondaryLabel="Cancel"
      onPrimaryClick={handlePrintClick}
      onSecondaryClick={onHide}
    >
      <div className="mb-3">
        <InputField
          id="due-date-picker"
          variant="date"
          label="Due date"
          fullWidth
          clearable={false}
          value={dueDateValue}
          onDateChange={setDueDate}
        />
      </div>

      <div className="d-flex flex-row h-100 justify-content-center">
        <div
          className="p-0 overflow-auto create-pdf-modal-box"
          style={{ maxWidth: '210mm' }}
        >
          <div
            ref={printRef}
            className="invoice-print-container position-relative"
          >
            <div className="print-page w-100 d-flex flex-column">
              <div className="invoice-print-header mb-3">
                <div className="d-flex justify-content-between align-items-start">
                  <div className="d-flex align-items-center">
                    <div className="invoice-logo-square me-3">
                      <img
                        src="/logo192.png"
                        alt="Logo"
                        className="invoice-logo-image"
                      />
                    </div>
                    <div>
                      <div className="fw-bold fs-5">Dental invoice</div>
                      {invoice.clinicName && (
                        <div className="invoice-clinic-name">
                          {invoice.clinicName}
                        </div>
                      )}
                      {(invoice.clinicAddress ||
                        invoice.clinicCity ||
                        invoice.clinicState ||
                        invoice.clinicZip) && (
                        <div className="invoice-clinic-address">
                          {invoice.clinicAddress}
                          {(invoice.clinicCity ||
                            invoice.clinicState ||
                            invoice.clinicZip) && (
                            <>
                              <br />
                              {[
                                invoice.clinicCity,
                                invoice.clinicState,
                                invoice.clinicZip,
                              ]
                                .filter(Boolean)
                                .join(', ')}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="text-end invoice-header-right">
                    <div className="invoice-issued-date">
                      Issued on{' '}
                      {new Date(invoice.invoiceDate).toLocaleDateString()}
                    </div>
                    <div className="invoice-number">
                      Invoice no. {formatInvoiceNumber(invoice.invoiceNo)}
                    </div>
                  </div>
                </div>
              </div>

              <div className="invoice-patient-info mb-3">
                <div className="mb-2 invoice-print-title">
                  Patient information
                </div>
                <div className="invoice-patient-name">
                  {invoice.patientName}
                </div>
                {(invoice.patientAddress ||
                  invoice.patientCity ||
                  invoice.patientState ||
                  invoice.patientZip) && (
                  <div className="invoice-patient-address">
                    {invoice.patientAddress}
                    {(invoice.patientCity ||
                      invoice.patientState ||
                      invoice.patientZip) && (
                      <>
                        <br />
                        {[
                          invoice.patientCity,
                          invoice.patientState,
                          invoice.patientZip,
                        ]
                          .filter(Boolean)
                          .join(', ')}
                      </>
                    )}
                  </div>
                )}
              </div>

              <div className="invoice-fee-breakdown flex-grow-1 d-flex flex-column">
                <div className="mb-3 invoice-print-title">Fee breakdown</div>

                <div className="flex-grow-1">
                  {!invoice.ledgerEntries ||
                  invoice.ledgerEntries.length === 0 ? (
                    <DataTable
                      className="invoice-print-procedure-table"
                      columns={PROCEDURE_COLUMNS}
                      rows={[]}
                      showActions={false}
                      state="empty"
                      emptyState={{
                        title: 'No fee breakdown available',
                        description: '',
                      }}
                    />
                  ) : (
                    invoice.ledgerEntries.map((entry) => (
                      <div
                        key={entry.appointmentId}
                        className="mb-4 invoice-appointment-entry"
                      >
                        <div className="mb-2 d-flex justify-content-between align-items-start invoice-appointment-header">
                          <div className="invoice-appointment-type">
                            Appointment type{' '}
                            <span className="fw-bold">
                              {entry.appointmentType || 'General'}
                            </span>
                          </div>
                          <div className="invoice-appointment-provider">
                            Provider{' '}
                            <span className="fw-bold">
                              {entry.providerName}
                            </span>{' '}
                            Date{' '}
                            <span className="fw-bold">
                              {new Date(
                                entry.appointmentDate,
                              ).toLocaleDateString()}
                            </span>
                          </div>
                        </div>

                        <DataTable
                          className="invoice-print-procedure-table"
                          columns={PROCEDURE_COLUMNS}
                          rows={buildProcedureRows(entry)}
                          showActions={false}
                          state={
                            entry.procedures.length === 0 ? 'empty' : 'default'
                          }
                          emptyState={{
                            title: 'No fee breakdown available',
                            description: '',
                          }}
                        />
                      </div>
                    ))
                  )}
                </div>
              </div>

              <DescriptionList
                className="invoice-print-summary mt-auto"
                variant="file-details"
                columns={1}
                items={[
                  {
                    label: 'Charges',
                    value: formatCurrency(invoice.charges),
                  },
                  {
                    label: 'Approved insurance',
                    value: `-${formatCurrency(invoice.approvedInsurance)}`,
                  },
                  {
                    label: 'Discount',
                    value: `-${formatCurrency(invoice.discount)}`,
                  },
                  {
                    label: 'Appointment adjustment',
                    value: `-${formatCurrency(invoice.appointmentAdjustment)}`,
                  },
                  {
                    label: 'Total',
                    value: formatCurrency(invoice.patientResponsibilityTotal),
                  },
                ]}
              />

              <div className="invoice-print-footer mt-5">
                <div className="mb-3 d-flex align-items-baseline gap-2">
                  <span className="fw-bold invoice-signature-label">
                    Patient Signature:
                  </span>
                  <div className="invoice-signature-line" />
                </div>
                <div className="d-flex flex-column">
                  <div className="invoice-report-date">
                    Due date {dueDate.toLocaleDateString()}
                  </div>
                </div>
              </div>
            </div>

            <style>
              {`
          @page { size: A4; margin: 8mm; }
          @media print {
            body { background: var(--ds-white); }
            .invoice-print-container { position: static !important; }
            .print-page {
              break-after: page;
              position: static !important;
              z-index: auto !important;
              width: auto !important;
            }
            .invoice-print-summary {
              margin-top: auto !important;
            }
          }
        `}
            </style>
          </div>
        </div>
      </div>
    </Modal>
  )
}

export default PrintInvoiceModal
